import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Select from 'react-select'
import { Button, CircularProgress, TextField } from '@material-ui/core'
import { OrganizationSelect } from './UserInfoPage'
import { useClient } from '../../api/clientContext'
import isEmail from 'validator/lib/isEmail'
import { useDispatch } from 'react-redux'
import { setFlashMsg } from '../../store/app/index'
import { OrganizationAccessRequest } from '../../types/apiTypes'
import { HelCheckbox } from '../../components/HelFormFields/HelCheckbox'

const OrganizationAccessRequestForm: React.FC<{
  userOrganizations: string[]
  organizations: OrganizationSelect[]
  organizationAccessRequests: OrganizationAccessRequest[]
  setRefreshing: (b: boolean) => void
}> = ({
  userOrganizations,
  organizations,
  organizationAccessRequests,
  setRefreshing,
}) => {
  const client = useClient()
  const intl = useIntl()
  const dispatch = useDispatch()
  const [selectedOrganization, setSelectedOrganization] =
    useState<OrganizationSelect | null>(null)
  const [orgsToBeShown, setOrgsToBeShown] = useState<OrganizationSelect[]>([])
  const [accessRequestEmail, setAccessRequestEmail] = useState<string>('')
  const [creatingNewOrg, setCreatingNewOrg] = useState<boolean>(false)
  const [orgName, setOrgName] = useState<string>('')

  // TODO: Should you use redux editor/setLoading instead?
  const [loading, setLoading] = useState(false)

  const formReady = (): boolean =>
    (!!selectedOrganization || (creatingNewOrg && orgName !== '')) &&
    isEmail(accessRequestEmail)

  useEffect(
    () =>
      setOrgsToBeShown(
        organizations.filter(
          (org) =>
            !userOrganizations.includes(org.id) &&
            !organizationAccessRequests.some(
              (request) => request.organization === org.id
            )
        )
      ),
    [organizations, userOrganizations, organizationAccessRequests]
  )

  const handleSubmitAccessRequest = async (): Promise<void> => {
    if (!client) {
      console.warn('Client not initialized')
      return
    }

    setLoading(true)

    try {
      if (creatingNewOrg) {
        await client.post('organizationcreationrequest', {
          name: orgName,
          email: accessRequestEmail,
        })
        dispatch(
          setFlashMsg({
            msg: 'organization-creation-request-sent',
            style: 'message',
            sticky: false,
          })
        )
      } else {
        await client.post('organizationaccessrequest', {
          organization: selectedOrganization?.id,
          email: accessRequestEmail,
        })
        dispatch(
          setFlashMsg({
            msg: 'organization-access-request-sent',
            style: 'message',
            sticky: false,
          })
        )

        setOrgsToBeShown(
          orgsToBeShown.filter((org) => org.id !== selectedOrganization?.id)
        )
      }
      setAccessRequestEmail('')
      setSelectedOrganization(null)
    } catch (error) {
      dispatch(
        setFlashMsg({
          msg: 'generic-error-message',
          style: 'error',
          sticky: false,
        })
      )
    }

    setRefreshing(true)
    setLoading(false)
  }

  return (
    <div className="organization-access-request-container">
      <div className="organization-picker">
        <p>
          <strong>{intl.formatMessage({ id: 'choose-organization' })}</strong>
        </p>
        <Select
          placeholder={intl.formatMessage({ id: 'choose-organization' })}
          options={orgsToBeShown}
          isDisabled={creatingNewOrg}
          onChange={(selectedOption) =>
            setSelectedOrganization(selectedOption as OrganizationSelect)
          }
          isOptionSelected={(option) => option.id === selectedOrganization?.id}
          value={selectedOrganization}
        />
      </div>
      <div>
        <HelCheckbox
          onChange={() => {
            setSelectedOrganization(null)
            setCreatingNewOrg(!creatingNewOrg)
          }}
          checked={creatingNewOrg}
          label={<FormattedMessage id="org-not-in-list" />}
        />
      </div>
      {creatingNewOrg && (
        <div style={{ paddingLeft: '16px' }}>
          <strong>
            <p style={{ paddingTop: '16px' }}>
              {intl.formatMessage({ id: 'new-org-name' })}
            </p>
          </strong>{' '}
          <TextField
            style={{ marginTop: '0px', paddingBottom: '32px' }}
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
          />
        </div>
      )}
      <div className="access-request-email">
        <p style={{ paddingTop: '16px' }}>
          <strong>
            {intl.formatMessage({ id: 'organization-access-request-email' })}
          </strong>
        </p>
        <TextField
          style={{ marginTop: '0px', paddingBottom: '32px' }}
          value={accessRequestEmail}
          onChange={(e) => setAccessRequestEmail(e.target.value)}
          onKeyPress={(e) =>
            e.key === 'Enter' && formReady() && handleSubmitAccessRequest()
          }
        />
        <Button
          disabled={loading || !formReady()}
          variant="contained"
          onClick={handleSubmitAccessRequest}
          endIcon={loading && <CircularProgress color="inherit" size={25} />}
          color="primary"
        >
          {intl.formatMessage({
            id: creatingNewOrg
              ? 'send-organization-creation-request'
              : 'send-organization-access-request',
          })}
        </Button>
      </div>
    </div>
  )
}

export default OrganizationAccessRequestForm
